import {
  BlockedContestWords,
  BlockedInappropriateLanguage,
  BlockedSoftWords,
} from '~/domains/common/constants/blockedLanguage.const';
import type { League } from '~/domains/commissioner/interfaces/league.interface';

import type { DynamicSection, BaseSection } from '../domains/common/sections/types';
import type { QueryParams } from '../domains/common/api/settings/types/query.params';
import type { TextFieldType } from '../domains/common/fields/StaticField/TextField';
import type { TextAreaFieldType } from '../domains/common/fields/StaticField/TextAreaField';
import type { SingleSelectField } from '../domains/common/fields/StaticField/SingleSelectField/types';
import { getBannerUploadField } from '../domains/common/api/banner/getBannerUploadField';

export function getCreateSectionsData(
  queryParams: QueryParams,
  contestData?: { description?: string }
) {
  const staticBasicFields: BaseSection = {
    type: 'BaseSection',
    key: 'basicInfo',
    fields: [
      {
        type: 'TextField',
        key: 'contestName',
        label: 'Contest title',
        isRequired: true,
        defaultValue: '',
        validation: {
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextFieldType,
      {
        type: 'TextArea',
        key: 'description',
        label: 'Contest description',
        defaultValue: contestData?.description || '',
        validation: {
          isOptional: true,
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextAreaFieldType,
      getBannerUploadField(),
    ],
  };

  const gameplaySection: DynamicSection = {
    type: 'DynamicSection',
    key: 'gameplay',
    endpointName: 'gameplay',
    queryParams: {
      ...queryParams,
      sectionType: 'gameplay',
      operation: 'create',
    },
  };

  const entriesSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'entries',
    endpointName: 'entries',
    queryParams: {
      ...queryParams,
      sectionType: 'entries',
      operation: 'create',
    },
  };

  const contestDetailsSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'contestDetails',
    endpointName: 'settings',
    queryParams: {
      ...queryParams,
      sectionType: 'contestDetails',
      operation: 'create',
    },
  };

  return [staticBasicFields, gameplaySection, entriesSection, contestDetailsSection];
}

export function getEditSectionsData(
  queryParams: QueryParams,
  contestData?: { name?: string; description?: string; banner_url?: string }
) {
  const staticBasicFields: BaseSection = {
    type: 'BaseSection',
    key: 'basicInfo',
    fields: [
      {
        type: 'TextField',
        key: 'contestName',
        label: 'Contest title',
        isRequired: true,
        defaultValue: contestData?.name || '',
        validation: {
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextFieldType,
      {
        type: 'TextArea',
        key: 'description',
        label: 'Contest description',
        defaultValue: contestData?.description || '',
        validation: {
          isOptional: true,
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextAreaFieldType,
      getBannerUploadField(contestData?.banner_url),
    ],
  };

  const gameplaySection: DynamicSection = {
    type: 'DynamicSection',
    key: 'gameplay',
    endpointName: 'gameplay',
    queryParams: {
      ...queryParams,
      sectionType: 'gameplay',
      operation: 'edit',
    },
  };

  const entriesSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'entries',
    endpointName: 'entries',
    queryParams: {
      ...queryParams,
      sectionType: 'entries',
      operation: 'edit',
    },
  };

  const contestDetailsSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'contestDetails',
    endpointName: 'settings',
    queryParams: {
      ...queryParams,
      sectionType: 'contestDetails',
      operation: 'edit',
    },
  };

  return [staticBasicFields, gameplaySection, entriesSection, contestDetailsSection];
}

export function getManageSectionsData(
  queryParams: QueryParams,
  contestData?: { name?: string; description?: string; banner_url?: string }
) {
  const staticBasicFields: BaseSection = {
    type: 'BaseSection',
    key: 'basicInfo',
    fields: [
      {
        type: 'TextField',
        key: 'contestName',
        label: 'Contest title',
        isRequired: true,
        defaultValue: contestData?.name || '',
        validation: {
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextFieldType,
      {
        type: 'TextArea',
        key: 'description',
        label: 'Contest description',
        defaultValue: contestData?.description || '',
        validation: {
          isOptional: true,
          bannedWords: BlockedContestWords,
          bannedWordsHidden: BlockedInappropriateLanguage,
          softWords: BlockedSoftWords,
        },
      } satisfies TextAreaFieldType,
      getBannerUploadField(contestData?.banner_url),
    ],
  };

  const gameplaySection: DynamicSection = {
    type: 'DynamicSection',
    key: 'gameplay',
    endpointName: 'gameplay',
    queryParams: {
      ...queryParams,
      sectionType: 'gameplay',
      operation: 'restart',
    },
  };

  const entriesSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'entries',
    endpointName: 'entries',
    queryParams: {
      ...queryParams,
      sectionType: 'entries',
      operation: 'restart',
    },
  };

  const contestDetailsSection: DynamicSection = {
    type: 'DynamicSection',
    key: 'contestDetails',
    endpointName: 'settings',
    queryParams: {
      ...queryParams,
      sectionType: 'contestDetails',
      operation: 'restart',
    },
  };

  return [staticBasicFields, gameplaySection, entriesSection, contestDetailsSection];
}

export function getInitialSectionsData(
  leagues: League[],
  data?: { leagueId?: string | null; contestTemplateId?: string | null },
  isUserAdmin = false
) {
  const visibleLeagues = leagues.filter((league) => isUserAdmin || league.isActive);
  const hasLeagueId = visibleLeagues.some((league) => league.id === data?.leagueId);
  const defaultLeagueId = hasLeagueId ? data?.leagueId : null;

  const initialSectionsData: BaseSection[] = [
    {
      type: 'BaseSection',
      key: 'init',
      fields: [
        {
          key: 'leagueId',
          label: 'Select a league',
          description: 'Select a league for your contest.',
          type: 'SingleSelectField',
          scrollToNextSection: true,
          defaultValue: defaultLeagueId,
          params: {
            span: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
          options: visibleLeagues.map((league) => ({
            label: league.name,
            value: league.id,
            params: {
              icon: league.icon,
              direction: 'column',
              ...(!league.isActive
                ? {
                    badge: 'adminOnly',
                  }
                : {}),
            },
          })),
        } satisfies SingleSelectField,
        {
          type: 'DynamicField',
          key: 'contestTemplateId',
          dependsOn: [
            {
              fieldKey: 'leagueId',
            },
          ],
          endpointName: 'contestTemplates',
        },
      ],
    },
  ];

  return initialSectionsData;
}
